import { useState, useEffect, useRef } from 'react';
import styles from './AnimatedText.module.css';

interface AnimatedTextProps {
  content: string;
  isInitialLoad: boolean;
  isComplete: boolean;
  onComplete?: () => void;
  speed?: number; // Characters per second
  htmlFormatted?: boolean;
}

const AnimatedText: React.FC<AnimatedTextProps> = ({
  content,
  isInitialLoad,
  isComplete,
  onComplete,
  speed = 30, // Default speed: 30 characters per second
  htmlFormatted = false
}) => {
  const [displayedContent, setDisplayedContent] = useState<string>(isInitialLoad || isComplete ? content : '');
  const [isAnimating, setIsAnimating] = useState<boolean>(!isInitialLoad && !isComplete);
  const contentRef = useRef<string>(content);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationCompleteRef = useRef<boolean>(isInitialLoad || isComplete);
  const [fadeTransition, setFadeTransition] = useState<boolean>(false);
  const isMobileRef = useRef<boolean>(window.innerWidth <= 768);
  const [isVisible, setIsVisible] = useState<boolean>(isInitialLoad || isComplete);

  // If content changes, update the ref and reset animation state
  useEffect(() => {
    contentRef.current = content;
    animationCompleteRef.current = isInitialLoad || isComplete;
    isMobileRef.current = window.innerWidth <= 768;
    
    // If we're in initial load, animation is complete, or isComplete is explicitly set to true, show full content
    if (isInitialLoad || isComplete) {
      setDisplayedContent(content);
      setIsAnimating(false);
      setFadeTransition(false);
      setIsVisible(true);
    } else {
      // For desktop: instant reveal with fade-in animation
      if (!isMobileRef.current) {
        setDisplayedContent(content);
        setIsVisible(false);
        // Start fade-in animation after a small delay
        setTimeout(() => {
          setIsVisible(true);
          setFadeTransition(true);
          // Notify when animation completes
          setTimeout(() => {
            setIsAnimating(false);
            animationCompleteRef.current = true;
            if (onComplete) onComplete();
          }, 300); // Animation duration
        }, 50);
      } else {
        // For mobile: keep the original character-by-character animation
        setDisplayedContent('');
        setIsAnimating(true);
        setFadeTransition(false);
        setIsVisible(true);
      }
    }
  }, [content, isInitialLoad, isComplete, onComplete]);

  // Handle the animation for mobile
  useEffect(() => {
    if (!isAnimating || !isMobileRef.current) return;

    // If using HTML formatting, we need a different approach
    if (htmlFormatted) {
      animateHtmlContent();
    } else {
      animatePlainContent();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAnimating, htmlFormatted]);

  // Animation for plain text (mobile only)
  const animatePlainContent = () => {
    let currentIndex = 0;
    const fullContent = contentRef.current;
    
    const animateNextChar = () => {
      if (currentIndex <= fullContent.length) {
        setDisplayedContent(fullContent.substring(0, currentIndex));
        currentIndex++;
        
        // Calculate delay based on character
        const delay = getCharacterDelay(fullContent[currentIndex - 1]);
        
        timeoutRef.current = setTimeout(animateNextChar, delay);
      } else {
        // Add a small fade transition when animation completes
        setFadeTransition(true);
        
        // Small delay before setting animation to complete
        setTimeout(() => {
          setIsAnimating(false);
          animationCompleteRef.current = true;
          if (onComplete) onComplete();
        }, 100);
      }
    };
    
    animateNextChar();
  };

  // Animation for HTML formatted content (mobile only)
  const animateHtmlContent = () => {
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = contentRef.current;
    
    // Extract text content
    const textContent = tempDiv.textContent || '';
    let currentIndex = 0;
    
    const animateNextChar = () => {
      if (currentIndex <= textContent.length) {
        // Calculate how much of the original HTML to show
        const visibleText = textContent.substring(0, currentIndex);
        
        // For very short content, we might need to show the full content
        // to avoid HTML parsing issues
        if (textContent.length < 10 && currentIndex > textContent.length / 2) {
          setDisplayedContent(contentRef.current);
          currentIndex = textContent.length;
        } else {
          // Get partial HTML with improved handling for mobile
          const partialHtml = getPartialHtml(contentRef.current, visibleText.length);
          setDisplayedContent(partialHtml);
        }
        
        currentIndex++;
        
        // Calculate delay based on character and device
        const delay = getCharacterDelay(textContent[currentIndex - 1]);
        
        timeoutRef.current = setTimeout(animateNextChar, delay);
      } else {
        // For mobile, just set the full content to avoid flickering
        if (isMobileRef.current) {
          setDisplayedContent(contentRef.current);
        }
        
        // Add a small fade transition when animation completes
        setFadeTransition(true);
        
        // Small delay before setting animation to complete
        setTimeout(() => {
          setIsAnimating(false);
          animationCompleteRef.current = true;
          if (onComplete) onComplete();
        }, 100);
      }
    };
    
    // For mobile, use a faster animation to reduce flickering
    if (isMobileRef.current) {
      // Use a batch approach for mobile to reduce DOM updates
      const batchSize = Math.max(5, Math.floor(textContent.length / 20));
      let currentBatch = 0;
      
      const animateNextBatch = () => {
        if (currentBatch * batchSize <= textContent.length) {
          const endIndex = Math.min((currentBatch + 1) * batchSize, textContent.length);
          const visibleText = textContent.substring(0, endIndex);
          
          // Get partial HTML with improved handling for mobile
          const partialHtml = getPartialHtml(contentRef.current, visibleText.length);
          setDisplayedContent(partialHtml);
          
          currentBatch++;
          
          // Use a consistent delay for batches on mobile
          const delay = 1000 / (speed * 2); // Faster on mobile
          
          timeoutRef.current = setTimeout(animateNextBatch, delay);
        } else {
          // Set the full content at the end
          setDisplayedContent(contentRef.current);
          
          // Add a small fade transition when animation completes
          setFadeTransition(true);
          
          // Small delay before setting animation to complete
          setTimeout(() => {
            setIsAnimating(false);
            animationCompleteRef.current = true;
            if (onComplete) onComplete();
          }, 100);
        }
      };
      
      animateNextBatch();
    } else {
      // Use character-by-character animation for desktop
      animateNextChar();
    }
  };

  // Get partial HTML that shows only up to a certain amount of text
  const getPartialHtml = (html: string, visibleTextLength: number): string => {
    // This is a more robust approach for handling HTML tags
    let textCount = 0;
    let result = '';
    let inTag = false;
    let inEntity = false;
    
    for (let i = 0; i < html.length; i++) {
      const char = html[i];
      
      // Handle HTML tags
      if (char === '<') inTag = true;
      
      // Handle HTML entities (like &quot;, &amp;, etc.)
      if (char === '&' && !inTag) inEntity = true;
      if (inEntity && char === ';') inEntity = false;
      
      // Only count visible characters (not tags or entities)
      if (!inTag && !inEntity && !['<', '>'].includes(char)) textCount++;
      
      // Always include the character in the result
      result += char;
      
      // End tag tracking
      if (char === '>') inTag = false;
      
      // If we've reached the desired text length and we're not in the middle of a tag or entity, break
      if (textCount >= visibleTextLength && !inTag && !inEntity) break;
    }
    
    // Make sure all opened tags are properly closed
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = result;
    
    // Find all open tags that need to be closed
    const openTags = [];
    const regex = /<([a-z0-9]+)[^>]*>/gi;
    const closedRegex = /<\/([a-z0-9]+)>/gi;
    
    let match;
    while ((match = regex.exec(result)) !== null) {
      const tagName = match[1].toLowerCase();
      openTags.push(tagName);
    }
    
    while ((match = closedRegex.exec(result)) !== null) {
      const tagName = match[1].toLowerCase();
      const index = openTags.lastIndexOf(tagName);
      if (index !== -1) {
        openTags.splice(index, 1);
      }
    }
    
    // Close any remaining open tags in reverse order
    for (let i = openTags.length - 1; i >= 0; i--) {
      result += `</${openTags[i]}>`;
    }
    
    return result;
  };

  // Get delay for specific characters to create a more natural typing effect
  const getCharacterDelay = (char: string | undefined): number => {
    if (!char) return 1000 / speed;
    
    // For mobile, use faster animation with less variation
    if (isMobileRef.current) {
      // Punctuation gets a slightly longer delay
      if (['.', '!', '?'].includes(char)) {
        return 1000 / (speed * 1.5); // 1.5x slower for end punctuation on mobile
      } else if ([',', ';', ':'].includes(char)) {
        return 1000 / (speed * 1.2); // 1.2x slower for mid-sentence punctuation on mobile
      }
      
      return 1000 / (speed * 1.5); // Faster base speed on mobile
    }
    
    // Desktop delays - faster with less variation
    // Punctuation gets a slightly longer delay
    if (['.', '!', '?'].includes(char)) {
      return 1000 / (speed / 2); // 2x slower for end punctuation (was 4x)
    } else if ([',', ';', ':'].includes(char)) {
      return 1000 / (speed / 1.5); // 1.5x slower for mid-sentence punctuation (was 2x)
    }
    
    return 1000 / speed;
  };

  // Determine if we should show the blinking cursor
  const showCursor = isAnimating && !isInitialLoad && !isComplete && isMobileRef.current;

  return (
    <div 
      className={`
        ${styles.animatedText} 
        ${showCursor ? styles.animating : ''} 
        ${fadeTransition ? styles.fadeTransition : ''} 
        ${!isVisible ? styles.hidden : styles.visible}
      `}
    >
      {htmlFormatted ? (
        <div dangerouslySetInnerHTML={{ __html: displayedContent }} />
      ) : (
        <>{displayedContent}</>
      )}
    </div>
  );
};

export default AnimatedText; 